(() => {
  const WEBSITE_SETTINGS_CONTROLLER_KEY =
    "__framewerkWebsiteSettingsController";
  const WEBSITE_SETTINGS_CACHE_KEY = "framewerk:website-settings:v1";
  const WEBSITE_SETTINGS_TIMEOUT_MS = 5000;
  const WEBSITE_SETTINGS_CACHE_TTL_MS = 60 * 1000;
  const PRODUCT_VARIANT_IMAGE_FOCUS_CONTROLLER_KEY =
    "__framewerkProductVariantImageFocusController";
  const FORMS_EMBED_CONTROLLER_KEY = "__framewerkFormsEmbedController";
  const PRODUCT_VARIANT_IMAGE_FOCUS_BREAKPOINT = "(max-width: 749px)";
  const SELECTED_PRODUCT_MEDIA_SELECTOR =
    '[data-testid="product-information-media"] slideshow-slide[aria-hidden="false"] .product-media[data-media-id]';
  const PRODUCT_MEDIA_GESTURE_SELECTOR =
    '[data-testid="product-information-media"], media-gallery';

  function installProductMediaExternalLinkGestureGuard() {
    if (window.__framewerkProductMediaGestureGuard) return;
    window.__framewerkProductMediaGestureGuard = true;
    let gesture = null;
    let lastDragAt = 0;

    document.addEventListener(
      "touchstart",
      (event) => {
        const touch = event.touches?.[0];
        const target = event.target;
        gesture =
          touch &&
          target instanceof Element &&
          target.closest(PRODUCT_MEDIA_GESTURE_SELECTOR)
            ? { x: touch.clientX, y: touch.clientY, dragged: false }
            : null;
      },
      { capture: true, passive: true },
    );
    document.addEventListener(
      "touchmove",
      (event) => {
        const touch = event.touches?.[0];
        if (!gesture || !touch) return;
        if (
          Math.abs(touch.clientX - gesture.x) > 10 ||
          Math.abs(touch.clientY - gesture.y) > 10
        ) {
          gesture.dragged = true;
        }
      },
      { capture: true, passive: true },
    );
    document.addEventListener(
      "touchend",
      () => {
        if (gesture?.dragged) lastDragAt = Date.now();
        gesture = null;
      },
      { capture: true, passive: true },
    );
    document.addEventListener(
      "click",
      (event) => {
        if (Date.now() - lastDragAt > 700) return;
        const link =
          event.target instanceof Element
            ? event.target.closest("a[href]")
            : null;
        if (!link) return;
        const destination = new URL(link.href, window.location.href);
        if (
          destination.origin === window.location.origin &&
          link.target !== "_blank"
        )
          return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true,
    );
  }

  installProductMediaExternalLinkGestureGuard();

  const HOMEPAGE_COLLECTION_LINKS = [
    {
      sectionSuffix: "__product_list_PJ9LUk",
      href: "/collections/new",
      label: "New Releases",
    },
    {
      sectionSuffix: "__product_list_fHpiA7",
      href: "/collections/coming-soon",
      label: "Coming Soon",
    },
  ];

  function installHomepageCollectionLinkCards() {
    if (
      typeof document.querySelector !== "function" ||
      typeof document.createElement !== "function"
    ) {
      return;
    }

    if (
      !document.getElementById?.("framewerk-homepage-collection-link-styles")
    ) {
      const styles = document.createElement("style");
      styles.id = "framewerk-homepage-collection-link-styles";
      styles.textContent = `
        .framewerk-collection-link-slide {
          height: auto !important;
          align-self: stretch;
        }
        .framewerk-collection-link-slide > .resource-list__item {
          height: 100%;
        }
        .framewerk-collection-link-card {
          display: grid;
          width: 100%;
          height: var(--framewerk-collection-card-height, 100%);
          min-height: 320px;
          place-items: center;
          padding: 28px;
          border: 3px solid #ef1515;
          border-radius: 24px;
          background: #fff;
          color: #151515;
          text-align: center;
          text-decoration: none;
          transition: background 160ms ease, color 160ms ease, transform 160ms ease;
        }
        .framewerk-collection-link-card:hover {
          background: #ef1515;
          color: #fff;
          transform: translateY(-2px);
        }
        .framewerk-collection-link-card:focus-visible {
          outline: 4px solid #ffd500;
          outline-offset: 3px;
        }
        .framewerk-collection-link-card__copy {
          display: grid;
          gap: 8px;
          justify-items: center;
          font-family: var(--font-heading--family, inherit);
          font-weight: 900;
          line-height: 0.92;
          text-transform: uppercase;
        }
        .framewerk-collection-link-card__copy strong {
          max-width: 6ch;
          font-size: clamp(2.5rem, 4vw, 4.75rem);
        }
        .framewerk-collection-link-card__copy small {
          font-size: 0.75rem;
          letter-spacing: 0.08em;
        }
        @media (max-width: 749px) {
          .framewerk-collection-link-card {
            min-height: 280px;
            padding: 20px;
          }
          .framewerk-collection-link-card__copy strong {
            font-size: clamp(2rem, 12vw, 3.5rem);
          }
        }
      `;
      document.head?.append(styles);
    }

    for (const config of HOMEPAGE_COLLECTION_LINKS) {
      const section = document.querySelector(`[id$="${config.sectionSuffix}"]`);
      if (!section) continue;
      const slides = section.querySelector(
        'slideshow-component[ref="resourceListCarousel"] slideshow-slides[ref="scroller"]',
      );
      if (!slides || slides.querySelector("[data-framewerk-collection-link]"))
        continue;

      const slide = document.createElement("slideshow-slide");
      const slideIndex = slides.querySelectorAll(
        ":scope > slideshow-slide",
      ).length;
      slide.className = "resource-list__slide framewerk-collection-link-slide";
      slide.setAttribute("ref", "slides[]");
      slide.setAttribute("data-framewerk-collection-link", config.label);
      slide.style.setProperty("--slideshow-timeline", `--slide-${slideIndex}`);

      const item = document.createElement("div");
      item.className = "resource-list__item";
      const link = document.createElement("a");
      link.className = "framewerk-collection-link-card";
      link.href = config.href;
      link.setAttribute("aria-label", `See more ${config.label}`);
      const copy = document.createElement("span");
      copy.className = "framewerk-collection-link-card__copy";
      const heading = document.createElement("strong");
      heading.textContent = "See more";
      const detail = document.createElement("small");
      detail.textContent = config.label;
      copy.append(heading, detail);
      link.append(copy);
      item.append(link);
      slide.append(item);
      slides.append(slide);

      const matchProductHeight = () => {
        const productCard = slides.querySelector(
          ":scope > slideshow-slide:not([data-framewerk-collection-link]) product-card",
        );
        const height = Math.ceil(
          productCard?.getBoundingClientRect().height ?? 0,
        );
        if (height > 0)
          link.style.setProperty(
            "--framewerk-collection-card-height",
            `${height}px`,
          );
      };
      requestAnimationFrame(() => requestAnimationFrame(matchProductHeight));
      window.addEventListener("load", matchProductHeight, { once: true });
      window.addEventListener("resize", matchProductHeight, { passive: true });
    }
  }

  installHomepageCollectionLinkCards();
  document.addEventListener?.("shopify:section:load", () =>
    installHomepageCollectionLinkCards(),
  );
  window.addEventListener?.("pageshow", () =>
    installHomepageCollectionLinkCards(),
  );

  function readSessionValue(key) {
    try {
      return JSON.parse(window.sessionStorage?.getItem(key) ?? "null");
    } catch {
      return null;
    }
  }

  function writeSessionValue(key, value) {
    try {
      window.sessionStorage?.setItem(key, JSON.stringify(value));
    } catch {
      // Session storage can be unavailable in privacy-restricted storefronts.
    }
  }

  async function fetchJson(url, options = {}, timeoutMs = 8000) {
    const controller =
      typeof AbortController === "function" ? new AbortController() : null;
    const timeout = controller
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;
    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        ...options,
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (!response.ok) {
        throw new Error(`Request failed (${response.status}).`);
      }
      return await response.json();
    } finally {
      if (timeout !== null) window.clearTimeout(timeout);
    }
  }

  function createWebsiteSettingsController() {
    let request = null;
    const apply = async (root = document) => {
      if (!request) {
        request = (async () => {
          const cacheEntry = readSessionValue(WEBSITE_SETTINGS_CACHE_KEY);
          const cached =
            cacheEntry?.data && typeof cacheEntry.storedAt === "number"
              ? cacheEntry
              : cacheEntry
                ? { data: cacheEntry, storedAt: 0 }
                : null;
          if (
            cached &&
            Date.now() - cached.storedAt < WEBSITE_SETTINGS_CACHE_TTL_MS
          ) {
            return cached.data;
          }
          try {
            const url = new URL("/apps/framewerk", window.location.origin);
            url.searchParams.set("resource", "website-access");
            const settings = await fetchJson(
              url,
              {
                cache: "no-store",
                headers: { Accept: "application/json" },
              },
              WEBSITE_SETTINGS_TIMEOUT_MS,
            );
            writeSessionValue(WEBSITE_SETTINGS_CACHE_KEY, {
              data: settings,
              storedAt: Date.now(),
            });
            return settings;
          } catch (error) {
            console.warn(
              "Framewerk Website settings could not be loaded.",
              error,
            );
            return cached?.data ?? { comparisonEnabled: false };
          }
        })();
      }
      const settings = await request;
      const comparisonEnabled = settings.comparisonEnabled !== false;
      document.documentElement.dataset.framewerkComparison = comparisonEnabled
        ? "enabled"
        : "disabled";
      root
        .querySelectorAll?.(
          "[data-framewerk-comparison]:not(html), [data-framewerk-comparison-toggle], compare-toggle",
        )
        .forEach((surface) =>
          surface.toggleAttribute("hidden", !comparisonEnabled),
        );
      return settings;
    };

    document.addEventListener?.("shopify:section:load", (event) => {
      const root =
        event.target instanceof HTMLElement ? event.target : document;
      void apply(root);
    });
    return Object.freeze({ apply });
  }

  function createFormsEmbedController() {
    const selector = 'iframe[src*="/forms/"]';
    const fallbackHeight = 960;
    const mounted = new WeakSet();
    const timers = new WeakMap();

    const formDetails = (frame) => {
      try {
        const url = new URL(frame.src, document.baseURI);
        const match = url.pathname.match(/\/forms\/([^/?#]+)/);
        return {
          origin: url.origin,
          publicId: match ? decodeURIComponent(match[1]) : "",
        };
      } catch {
        return null;
      }
    };

    const requestHeight = (frame) => {
      const details = formDetails(frame);
      if (!details || !frame.contentWindow) return;
      try {
        frame.contentWindow.postMessage(
          {
            type: "framewerk:request-form-height",
            publicId: details.publicId,
          },
          details.origin,
        );
      } catch {
        // The generous fallback still keeps the complete form available.
      }
    };

    const releaseClippingAncestors = (frame, height) => {
      let ancestor = frame.parentElement;
      let depth = 0;
      while (ancestor && ancestor !== document.body && depth < 4) {
        const styles = window.getComputedStyle(ancestor);
        const overflow = styles.overflowY || styles.overflow;
        if (
          ancestor.clientHeight > 0 &&
          ancestor.clientHeight + 4 < height &&
          (overflow === "hidden" || overflow === "clip")
        ) {
          ancestor.style.setProperty("height", "auto", "important");
          ancestor.style.setProperty("max-height", "none", "important");
          ancestor.style.setProperty("overflow", "visible", "important");
        }
        ancestor = ancestor.parentElement;
        depth += 1;
      }
    };

    const setHeight = (frame, height) => {
      const nextHeight = Math.min(5000, Math.max(240, Math.ceil(height) + 2));
      frame.style.setProperty("height", `${nextHeight}px`, "important");
      frame.style.setProperty("min-height", "240px", "important");
      frame.setAttribute("height", String(nextHeight));
      releaseClippingAncestors(frame, nextHeight);
    };

    const mount = (frame) => {
      if (
        typeof HTMLIFrameElement === "undefined" ||
        !(frame instanceof HTMLIFrameElement) ||
        mounted.has(frame)
      )
        return;
      mounted.add(frame);
      frame.style.setProperty("width", "100%", "important");
      frame.style.setProperty("height", `${fallbackHeight}px`, "important");
      frame.style.setProperty("min-height", "720px", "important");
      frame.style.setProperty("max-height", "none", "important");
      frame.style.setProperty("display", "block", "important");
      frame.style.setProperty("border", "0", "important");
      frame.style.setProperty("overflow", "hidden", "important");
      frame.style.setProperty("background", "transparent", "important");
      frame.setAttribute("scrolling", "no");
      frame.setAttribute("allowtransparency", "true");
      releaseClippingAncestors(frame, fallbackHeight);
      const request = () => requestHeight(frame);
      frame.addEventListener("load", request);
      timers.set(
        frame,
        [0, 100, 350, 900, 1800, 3500].map((delay) =>
          window.setTimeout(request, delay),
        ),
      );
    };

    const apply = (root = document) => {
      if (
        typeof HTMLIFrameElement !== "undefined" &&
        root instanceof HTMLIFrameElement &&
        root.matches(selector)
      ) {
        mount(root);
      }
      root.querySelectorAll?.(selector).forEach(mount);
    };

    const onMessage = (event) => {
      const payload = event.data;
      if (!payload || payload.type !== "framewerk:form-height") return;
      const frame = Array.from(document.querySelectorAll(selector)).find(
        (candidate) => candidate.contentWindow === event.source,
      );
      if (!frame) return;
      const details = formDetails(frame);
      const height = Number(payload.height);
      if (
        !details ||
        event.origin !== details.origin ||
        (details.publicId && payload.publicId !== details.publicId) ||
        !Number.isFinite(height) ||
        height <= 0
      )
        return;
      timers.get(frame)?.forEach((timer) => window.clearTimeout(timer));
      timers.delete(frame);
      setHeight(frame, height);
    };

    window.addEventListener?.("message", onMessage);
    window.addEventListener?.("resize", () => apply(document), {
      passive: true,
    });
    document.addEventListener?.("shopify:section:load", (event) =>
      apply(
        typeof Element !== "undefined" && event.target instanceof Element
          ? event.target
          : document,
      ),
    );
    if (typeof MutationObserver !== "undefined" && document.documentElement) {
      new MutationObserver((records) => {
        records.forEach((record) =>
          record.addedNodes.forEach((node) => {
            if (typeof Element !== "undefined" && node instanceof Element)
              apply(node);
          }),
        );
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
    return Object.freeze({ apply });
  }

  if (typeof HTMLIFrameElement !== "undefined") {
    const formsEmbedController =
      window[FORMS_EMBED_CONTROLLER_KEY] ?? createFormsEmbedController();
    window[FORMS_EMBED_CONTROLLER_KEY] = formsEmbedController;
    formsEmbedController.apply(document);
  }

  const websiteSettingsController =
    window[WEBSITE_SETTINGS_CONTROLLER_KEY] ??
    createWebsiteSettingsController();
  window[WEBSITE_SETTINGS_CONTROLLER_KEY] = websiteSettingsController;
  void websiteSettingsController.apply(document);

  function createProductVariantImageFocusController() {
    let focusRequest = 0;

    const isMobileProductPage = () =>
      /^\/products(?:\/|$)/.test(window.location.pathname) &&
      window.matchMedia?.(PRODUCT_VARIANT_IMAGE_FOCUS_BREAKPOINT)?.matches ===
        true;

    const selectedProductMedia = () =>
      Array.from(
        document.querySelectorAll?.(SELECTED_PRODUCT_MEDIA_SELECTOR) ?? [],
      ).find((media) => {
        const bounds = media.getBoundingClientRect();
        return bounds.width > 0 && bounds.height > 0;
      }) ?? null;

    const requestedVariantId = (picker, control) => {
      const selectedOption = control.selectedOptions?.[0];
      const checkedOption = picker.querySelector?.(
        "input:checked[data-variant-id], option:checked[data-variant-id]",
      );
      return String(
        control.dataset?.variantId ??
          selectedOption?.dataset?.variantId ??
          checkedOption?.dataset?.variantId ??
          "",
      );
    };

    const currentUrlVariantId = () =>
      new URL(window.location.href).searchParams.get("variant") ?? "";

    const centreSelectedMedia = (request) => {
      const afterPaint =
        window.requestAnimationFrame?.bind(window) ??
        ((callback) => window.setTimeout(callback, 0));
      afterPaint(() => {
        afterPaint(() => {
          if (request !== focusRequest || !isMobileProductPage()) return;
          const media = selectedProductMedia();
          if (!media) return;
          media.scrollIntoView({
            behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)")
              ?.matches
              ? "auto"
              : "smooth",
            block: "center",
            inline: "nearest",
          });
        });
      });
    };

    const queueFocus = (picker, control) => {
      if (!isMobileProductPage()) return;
      const request = ++focusRequest;
      const initialMedia = selectedProductMedia();
      const initialMediaId = initialMedia?.dataset?.mediaId ?? "";
      const initialVariantId = currentUrlVariantId();
      const targetVariantId = requestedVariantId(picker, control);
      const startedAt = Date.now();
      let readyAt = 0;

      const check = () => {
        if (request !== focusRequest || !isMobileProductPage()) return;
        const media = selectedProductMedia();
        const mediaChanged = Boolean(
          media &&
          (media !== initialMedia ||
            !initialMedia?.isConnected ||
            media.dataset?.mediaId !== initialMediaId),
        );
        const urlVariantId = currentUrlVariantId();
        const variantChanged = targetVariantId
          ? urlVariantId === targetVariantId
          : urlVariantId !== initialVariantId;
        const elapsed = Date.now() - startedAt;

        if (media && (mediaChanged || variantChanged)) {
          readyAt ||= Date.now();
          if (mediaChanged || Date.now() - readyAt >= 180) {
            centreSelectedMedia(request);
            return;
          }
        }

        if (media && elapsed >= 4000) {
          centreSelectedMedia(request);
          return;
        }
        window.setTimeout(check, 80);
      };

      window.setTimeout(check, 0);
    };

    document.addEventListener(
      "change",
      (event) => {
        if (!(event.target instanceof Element)) return;
        const control = event.target.closest("input, select");
        const picker = control?.closest("variant-picker");
        if (!control || !picker) return;
        queueFocus(picker, control);
      },
      true,
    );

    return Object.freeze({ queueFocus });
  }

  const productVariantImageFocusController =
    window[PRODUCT_VARIANT_IMAGE_FOCUS_CONTROLLER_KEY] ??
    createProductVariantImageFocusController();
  window[PRODUCT_VARIANT_IMAGE_FOCUS_CONTROLLER_KEY] =
    productVariantImageFocusController;

  if (window.Framewerk?.version) return;

  const VERSION = "2.0.8";
  const KEYS = Object.freeze({
    vehicle: "framewerk:vehicle:v1",
    garage: "framewerk:garage:v1",
    comparison: "framewerk:comparison:v1",
    quote: "framewerk:quote:v1",
  });
  const EVENTS = Object.freeze({
    ready: "framewerk:ready",
    vehicleChanged: "framewerk:vehicle:changed",
    garageChanged: "framewerk:garage:changed",
    comparisonChanged: "framewerk:comparison:changed",
    quoteChanged: "framewerk:quote:changed",
    sectionRendered: "framewerk:lifecycle:section-rendered",
    catalogueState: "framewerk:catalogue:state",
  });
  const CATALOGUE_FILTER_SUFFIX =
    ".framewerk_catalogue_spike.compatibility_tokens";
  const CATALOGUE_FILTER_PARAM =
    "filter.p.m.framewerk_catalogue_spike.compatibility_tokens";
  const CATALOGUE_VEHICLE_PREFIX = "vehicle:";
  const CATALOGUE_UNIVERSAL_TOKEN = "universal";
  const VEHICLE_CACHE_KEY = "framewerk:vehicle-catalogue:v1";
  const VEHICLE_CACHE_TTL_MS = 5 * 60 * 1000;
  const VEHICLE_REQUEST_KEY = "__framewerkVehicleCatalogueRequest";

  const listeners = new Map();
  const legacyVehicle = read("framewerk:mister-fitment:selected-vehicle", null);
  const state = {
    vehicle: read(KEYS.vehicle, legacyVehicle),
    garage: read(KEYS.garage, []),
    comparison: read(KEYS.comparison, []),
    quote: read(KEYS.quote, []),
  };

  function read(key, fallback) {
    try {
      const value = JSON.parse(localStorage.getItem(key));
      return value ?? fallback;
    } catch {
      return fallback;
    }
  }

  function write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error("Framewerk storage write failed", { key, error });
      return false;
    }
  }

  function notifyListener(event, listener, detail) {
    try {
      listener(detail);
    } catch (error) {
      console.error("Framewerk event listener failed", { event, error });
    }
  }

  function publish(event, detail) {
    for (const listener of listeners.get(event) ?? []) {
      notifyListener(event, listener, detail);
    }
    window.dispatchEvent(new CustomEvent(event, { detail }));
  }

  function subscribe(event, listener, options = {}) {
    const group = listeners.get(event) ?? new Set();
    group.add(listener);
    listeners.set(event, group);
    if (options.replayCurrent) {
      if (event === EVENTS.vehicleChanged) {
        notifyListener(event, listener, state.vehicle);
      }
      if (event === EVENTS.garageChanged) {
        notifyListener(event, listener, [...state.garage]);
      }
      if (event === EVENTS.comparisonChanged) {
        notifyListener(event, listener, [...state.comparison]);
      }
      if (event === EVENTS.quoteChanged) {
        notifyListener(event, listener, [...state.quote]);
      }
    }
    return () => group.delete(listener);
  }

  function stableVehicle(vehicle) {
    if (!vehicle || typeof vehicle !== "object") return null;
    const code = String(vehicle.vehicleCode ?? vehicle.code ?? "").trim();
    if (!code) throw new Error("Framewerk vehicle requires a permanent code.");
    return {
      id: vehicle.id ?? null,
      key: vehicle.key ?? null,
      code,
      vehicleCode: code,
      make: String(vehicle.make ?? ""),
      model: String(vehicle.model ?? ""),
      series: String(vehicle.series ?? ""),
      variant: String(vehicle.variant ?? ""),
      yearFrom: vehicle.yearFrom ?? null,
      yearTo: vehicle.yearTo ?? null,
      landingPageUrl: vehicle.landingPageUrl ?? null,
      bannerImageUrl: vehicle.bannerImageUrl ?? null,
      label: String(
        vehicle.label ??
          [vehicle.make, vehicle.model, vehicle.series, vehicle.variant]
            .filter(Boolean)
            .join(" "),
      ),
    };
  }

  function vehicleIdentity(vehicle) {
    return [vehicle?.make, vehicle?.model, vehicle?.series, vehicle?.variant]
      .map((value) =>
        String(value ?? "")
          .trim()
          .toLowerCase(),
      )
      .join("|");
  }

  function readVehicleCatalogueCache() {
    try {
      const cached = JSON.parse(
        window.sessionStorage?.getItem(VEHICLE_CACHE_KEY),
      );
      if (
        !cached ||
        typeof cached.storedAt !== "number" ||
        Date.now() - cached.storedAt > VEHICLE_CACHE_TTL_MS ||
        !cached.data ||
        !Array.isArray(cached.data.vehicles)
      ) {
        return null;
      }
      return cached.data;
    } catch {
      return null;
    }
  }

  function writeVehicleCatalogueCache(data) {
    if (!data || !Array.isArray(data.vehicles)) return;
    try {
      window.sessionStorage?.setItem(
        VEHICLE_CACHE_KEY,
        JSON.stringify({ storedAt: Date.now(), data }),
      );
    } catch {
      // Session storage can be unavailable in privacy-restricted storefronts.
      return undefined;
    }
  }

  function vehicleCatalogueRequest() {
    const pending = window[VEHICLE_REQUEST_KEY];
    if (pending && typeof pending.then === "function") return pending;

    let request;
    request = fetchJson(
      "/apps/framewerk?resource=vehicles",
      {
        cache: "no-store",
        headers: { Accept: "application/json" },
      },
      6000,
    )
      .then((data) => {
        writeVehicleCatalogueCache(data);
        return data;
      })
      .finally(() => {
        if (window[VEHICLE_REQUEST_KEY] === request) {
          delete window[VEHICLE_REQUEST_KEY];
        }
      });
    window[VEHICLE_REQUEST_KEY] = request;
    return request;
  }

  async function reconcileStoredVehicle(catalogue = null) {
    if (!state.vehicle) return false;
    const previous = JSON.stringify(state.vehicle);
    try {
      let data = catalogue ?? readVehicleCatalogueCache();
      if (!data) {
        data = await vehicleCatalogueRequest();
        if (!data) return false;
      }
      const vehicles = Array.isArray(data.vehicles) ? data.vehicles : [];
      if (!vehicles.length) return false;

      const identity = vehicleIdentity(state.vehicle);
      const hasSemanticIdentity = identity.replaceAll("|", "").length > 0;
      const semanticMatch = hasSemanticIdentity
        ? vehicles.find((vehicle) => vehicleIdentity(vehicle) === identity)
        : null;
      const codeMatch = vehicles.find(
        (vehicle) =>
          String(vehicle.code ?? vehicle.vehicleCode ?? "") ===
          String(state.vehicle.code ?? ""),
      );
      const canonical = semanticMatch || codeMatch || null;

      if (!canonical) {
        state.vehicle = null;
        write(KEYS.vehicle, null);
        write("framewerk:mister-fitment:selected-vehicle", null);
        return previous !== JSON.stringify(state.vehicle);
      }

      const previousCode = String(state.vehicle.code ?? "");
      state.vehicle = stableVehicle(canonical);
      write(KEYS.vehicle, state.vehicle);
      write("framewerk:mister-fitment:selected-vehicle", {
        ...canonical,
        label: state.vehicle.label,
      });
      if (previousCode !== state.vehicle.code) {
        console.info("Framewerk corrected a stale vehicle code", {
          previousCode,
          vehicleCode: state.vehicle.code,
          vehicle: state.vehicle.label,
        });
      }
      return previous !== JSON.stringify(state.vehicle);
    } catch (error) {
      console.warn("Framewerk could not verify the stored vehicle", error);
      return false;
    }
  }

  function stableProduct(product) {
    if (typeof product === "string") return { handle: product };
    if (!product || typeof product !== "object") {
      throw new Error("Framewerk product is required.");
    }
    const handle = String(product.handle ?? "").trim();
    if (!handle) throw new Error("Framewerk product requires a handle.");
    return {
      handle,
      productId: product.productId ? String(product.productId) : null,
      variantId: product.variantId ? String(product.variantId) : null,
      title: String(product.title ?? ""),
      url: String(product.url ?? `/products/${handle}`),
      image: String(product.image ?? ""),
      quantity: Math.max(1, Number(product.quantity) || 1),
    };
  }

  function isManagedCatalogueToken(value) {
    return (
      value === CATALOGUE_UNIVERSAL_TOKEN ||
      value.startsWith(CATALOGUE_VEHICLE_PREFIX)
    );
  }

  function findCatalogueFilterParam(url = new URL(window.location.href)) {
    for (const key of url.searchParams.keys()) {
      if (key.endsWith(CATALOGUE_FILTER_SUFFIX)) return key;
    }
    const input = document.querySelector?.(
      `input[name$="${CATALOGUE_FILTER_SUFFIX}"]`,
    );
    const root = document.querySelector?.("[data-mf-catalogue-spike]");
    return root?.dataset?.filterParam || input?.name || CATALOGUE_FILTER_PARAM;
  }

  function buildCatalogueUrl(vehicle, currentHref = window.location.href) {
    const url = new URL(currentHref, window.location.origin);
    const filterParam = findCatalogueFilterParam(url);
    const code = vehicle?.code ? String(vehicle.code).trim() : null;
    const legacyParams = [...url.searchParams.keys()].filter((key) => {
      const normalised = key.toLowerCase();
      return (
        normalised.startsWith("filter.p.m.") &&
        (normalised.includes("compatible_vehicles") ||
          normalised.includes("compatible-vehicles"))
      );
    });
    legacyParams.forEach((key) => url.searchParams.delete(key));

    const current = url.searchParams.getAll(filterParam);
    const preserved = current.filter(
      (value) => !isManagedCatalogueToken(value),
    );
    const desired = code
      ? [...preserved, `${CATALOGUE_VEHICLE_PREFIX}${code}`]
      : preserved;
    const uniqueDesired = [...new Set(desired)];
    const changed =
      legacyParams.length > 0 ||
      JSON.stringify([...new Set(current)].sort()) !==
        JSON.stringify([...uniqueDesired].sort());

    url.searchParams.delete(filterParam);
    uniqueDesired.forEach((value) =>
      url.searchParams.append(filterParam, value),
    );
    if (changed) url.searchParams.delete("page");
    return { url, changed, filterParam, code };
  }

  function isCataloguePage() {
    const pathname = String(window.location?.pathname ?? "");
    // Full-text search owns its query and relevance. Applying a saved vehicle
    // after Shopify has rendered results causes a late, contradictory rewrite.
    return pathname.startsWith("/collections/") || pathname === "/collections";
  }

  function normaliseStorefrontPath(value) {
    if (!value) return "";
    try {
      const url = new URL(String(value), window.location.origin);
      return decodeURIComponent(url.pathname).replace(/\/+$/, "") || "/";
    } catch {
      return "";
    }
  }

  async function collectionVehicleConflict(selectedVehicle) {
    if (!selectedVehicle || !isCataloguePage()) return null;
    try {
      const catalogue =
        readVehicleCatalogueCache() ?? (await vehicleCatalogueRequest());
      const currentPath = normaliseStorefrontPath(window.location.pathname);
      const collectionVehicle = (catalogue?.vehicles ?? []).find(
        (vehicle) =>
          normaliseStorefrontPath(vehicle?.landingPageUrl) === currentPath,
      );
      if (!collectionVehicle) return null;

      const selectedCode = String(selectedVehicle.code ?? "").trim();
      const collectionCode = String(
        collectionVehicle.code ?? collectionVehicle.vehicleCode ?? "",
      ).trim();
      if (!selectedCode || !collectionCode || selectedCode === collectionCode) {
        return null;
      }
      return {
        selectedVehicle,
        collectionVehicle: stableVehicle(collectionVehicle),
      };
    } catch (error) {
      console.warn("Framewerk could not verify the collection vehicle", error);
      return null;
    }
  }

  function removeVehicleConflictPrompt() {
    document
      .querySelectorAll?.("[data-framewerk-vehicle-conflict]")
      .forEach((prompt) => prompt.remove());
  }

  function renderVehicleConflictPrompt(conflict) {
    removeVehicleConflictPrompt();
    if (!conflict) return;

    const facets = catalogueFacetController();
    const anchor = facets ?? document.querySelector?.("main");
    if (!anchor?.parentNode) return;

    if (!document.getElementById?.("framewerk-vehicle-conflict-styles")) {
      const styles = document.createElement("style");
      styles.id = "framewerk-vehicle-conflict-styles";
      styles.textContent = `
        [data-framewerk-vehicle-conflict]{display:flex;align-items:center;justify-content:space-between;gap:16px;box-sizing:border-box;width:100%;margin:0 0 16px;padding:14px 16px;border:1px solid rgb(215 25 32 / .38);border-left:5px solid #d71920;border-radius:14px;background:#fff7f7;color:#191b16;font:inherit}
        [data-framewerk-vehicle-conflict] strong{display:block;margin:0 0 2px;font-weight:800}
        [data-framewerk-vehicle-conflict] p{margin:0;line-height:1.4}
        [data-framewerk-vehicle-conflict] button{flex:0 0 auto;min-height:44px;padding:10px 16px;border:0;border-radius:11px;background:#151913;color:#fff;font:inherit;font-weight:800;cursor:pointer}
        [data-framewerk-vehicle-conflict] button:hover{background:#d71920}
        @media(max-width:749px){[data-framewerk-vehicle-conflict]{align-items:stretch;flex-direction:column}[data-framewerk-vehicle-conflict] button{width:100%}}
      `;
      document.head?.append(styles);
    }

    const selectedLabel =
      conflict.selectedVehicle.label || conflict.selectedVehicle.code;
    const collectionLabel =
      conflict.collectionVehicle.label || conflict.collectionVehicle.code;
    const clearLabel =
      conflict.selectedVehicle.series ||
      conflict.selectedVehicle.model ||
      conflict.selectedVehicle.code;
    const prompt = document.createElement("section");
    prompt.dataset.framewerkVehicleConflict = "true";
    prompt.setAttribute("role", "status");

    const copy = document.createElement("div");
    const title = document.createElement("strong");
    title.textContent = "Vehicle filter conflict";
    const message = document.createElement("p");
    message.textContent = `This is the ${collectionLabel} collection, but ${selectedLabel} is selected. Clear the saved filter to see this collection correctly.`;
    copy.append(title, message);

    const clear = document.createElement("button");
    clear.type = "button";
    clear.textContent = `Clear ${clearLabel} filter`;
    clear.addEventListener("click", () => window.Framewerk?.vehicle?.clear());
    prompt.append(copy, clear);
    anchor.parentNode.insertBefore(prompt, anchor);
  }

  function catalogueFacetController() {
    const forms = [
      ...(document.querySelectorAll?.("facets-form-component[section-id]") ??
        []),
    ];
    return (
      forms.find((form) => !form.closest?.("dialog") && form.isConnected) ||
      forms.find((form) => form.isConnected) ||
      null
    );
  }

  function isCatalogueControlName(name) {
    return (
      name.startsWith("filter.") ||
      name === "sort_by" ||
      name === "q" ||
      name.startsWith("options[")
    );
  }

  function liveCatalogueUrl(currentHref = window.location.href) {
    const url = new URL(currentHref, window.location.origin);
    const facets = catalogueFacetController();
    const controls = [
      ...(facets?.querySelectorAll?.("input[name], select[name], textarea[name]") ??
        []),
    ].filter((control) => {
      const name = String(control?.name ?? "").trim();
      return name && !control.disabled && isCatalogueControlName(name);
    });
    if (!controls.length) return url;

    const names = new Set(
      controls.map((control) => String(control.name).trim()),
    );
    names.forEach((name) => url.searchParams.delete(name));

    controls.forEach((control) => {
      const name = String(control.name).trim();
      const type = String(control.type ?? "").toLowerCase();
      if (["checkbox", "radio"].includes(type) && !control.checked) return;

      if (type === "select-multiple" && control.selectedOptions) {
        [...control.selectedOptions].forEach((option) => {
          const value = String(option?.value ?? "");
          if (value) url.searchParams.append(name, value);
        });
        return;
      }

      const value = String(control.value ?? "");
      if (value) url.searchParams.append(name, value);
    });
    return url;
  }

  function setCatalogueState(status, message = "", detail = {}) {
    document.querySelectorAll?.("[data-mf-catalogue-spike]").forEach((root) => {
      root.dataset.state = status;
      root.dataset.ready = "true";
      const output = root.querySelector?.("[data-mf-catalogue-spike-status]");
      if (output) {
        output.hidden = !message;
        output.textContent = message;
      }
    });
    if (document.documentElement?.dataset) {
      document.documentElement.dataset.mfCatalogueProjectionState =
        status.toUpperCase();
    }
    publish(EVENTS.catalogueState, { status, message, ...detail });
  }

  let catalogueBusy = false;
  let catalogueQueued = false;
  let catalogueQueuedVehicle = null;
  let catalogueQueuedTarget = null;
  let catalogueActiveTarget = null;

  async function waitForCatalogueFacetController(timeoutMs = 800) {
    let facets = catalogueFacetController();
    if (typeof facets?.updateFiltersByURL === "function") return facets;

    const startedAt = Date.now();
    if (window.customElements?.whenDefined) {
      await Promise.race([
        window.customElements.whenDefined("facets-form-component"),
        new Promise((resolve) => window.setTimeout(resolve, 250)),
      ]);
    }

    while (Date.now() - startedAt < timeoutMs) {
      facets = catalogueFacetController();
      if (typeof facets?.updateFiltersByURL === "function") return facets;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    return catalogueFacetController();
  }

  async function applyCatalogueVehicle(vehicle) {
    if (!isCataloguePage()) return { changed: false, skipped: true };
    if (vehicle && !String(vehicle.code ?? "").trim()) {
      setCatalogueState(
        "failed",
        "Compatibility filtering is unavailable because this vehicle has no permanent code.",
      );
      return { changed: false, failed: true };
    }

    const conflict = await collectionVehicleConflict(vehicle);
    renderVehicleConflictPrompt(conflict);
    if (conflict) {
      setCatalogueState("conflict", "", {
        selectedVehicleCode: conflict.selectedVehicle.code,
        collectionVehicleCode: conflict.collectionVehicle.code,
      });
      return { changed: false, skipped: true, conflict: true };
    }

    // Shopify can replace the facet results before its History API update is
    // visible to another event handler. Merge the live form controls so a
    // category selected immediately before a vehicle is never lost.
    const result = buildCatalogueUrl(vehicle, liveCatalogueUrl().toString());
    if (!result.changed) {
      setCatalogueState("current", "", { url: result.url.toString() });
      return result;
    }

    // The active controller is normally ready immediately. On first load, use
    // a short bounded wait so a late custom-element upgrade does not force a
    // premature full-page navigation or hold the storefront for two seconds.
    const facets = await waitForCatalogueFacetController();

    setCatalogueState("updating", "Updating compatible products…", {
      url: result.url.toString(),
    });
    if (typeof facets?.updateFiltersByURL !== "function") {
      window.location.assign(result.url.toString());
      return result;
    }

    try {
      await facets.updateFiltersByURL(result.url.toString());
      setCatalogueState("current", "", { url: result.url.toString() });
      return result;
    } catch (error) {
      setCatalogueState(
        "failed",
        "Shopify could not refresh compatible products. Retry or clear the selected vehicle.",
        { error },
      );
      console.error("Framewerk catalogue filtering failed", error);
      return { ...result, failed: true };
    }
  }

  async function flushCatalogueVehicle() {
    if (catalogueBusy) return;
    catalogueBusy = true;
    try {
      while (catalogueQueued) {
        const vehicle = catalogueQueuedVehicle;
        catalogueActiveTarget = catalogueQueuedTarget;
        catalogueQueued = false;
        catalogueQueuedTarget = null;
        await applyCatalogueVehicle(vehicle);
      }
    } finally {
      catalogueBusy = false;
      catalogueActiveTarget = null;
    }
  }

  function queueCatalogueVehicle(vehicle) {
    const target = isCataloguePage()
      ? buildCatalogueUrl(vehicle, liveCatalogueUrl().toString()).url.toString()
      : null;
    if (
      target &&
      (target === catalogueActiveTarget ||
        (catalogueQueued && target === catalogueQueuedTarget))
    ) {
      return;
    }
    catalogueQueuedVehicle = vehicle;
    catalogueQueuedTarget = target;
    catalogueQueued = true;
    void flushCatalogueVehicle();
  }

  function collection(name, key, event, limit) {
    return Object.freeze({
      get: () => [...state[name]],
      has: (handle) => state[name].some((item) => item.handle === handle),
      add(product) {
        const item = stableProduct(product);
        if (this.has(item.handle)) return this.get();
        if (state[name].length >= limit) {
          throw new Error(`Framewerk ${name} limit of ${limit} reached.`);
        }
        state[name] = [...state[name], item];
        write(key, state[name]);
        publish(event, this.get());
        return this.get();
      },
      remove(handle) {
        state[name] = state[name].filter((item) => item.handle !== handle);
        write(key, state[name]);
        publish(event, this.get());
        return this.get();
      },
      update(handle, changes) {
        state[name] = state[name].map((item) =>
          item.handle === handle
            ? stableProduct({ ...item, ...changes })
            : item,
        );
        write(key, state[name]);
        publish(event, this.get());
        return this.get();
      },
      clear() {
        state[name] = [];
        write(key, state[name]);
        publish(event, []);
      },
      subscribe: (listener, options) => subscribe(event, listener, options),
      limit,
    });
  }

  function updateVehiclePresentation(root = document) {
    const vehicle = state.vehicle;
    if (document.body?.dataset) {
      document.body.dataset.activeVehicle = vehicle ? "true" : "false";
    }
    root
      .querySelectorAll?.("[data-active-vehicle-label]")
      .forEach((element) => {
        if (!vehicle) {
          element.textContent = element.dataset.activeVehicleLabelDefault ?? "";
          return;
        }

        const labelMode =
          element.dataset.mfSelectedLabelMode ||
          element.closest?.("[data-mf-selected-label-mode]")?.dataset
            ?.mfSelectedLabelMode;
        if (labelMode === "series_model") {
          element.textContent =
            [vehicle.series, vehicle.model]
              .map((value) => String(value ?? "").trim())
              .filter(Boolean)
              .join(" ") || vehicle.label;
          return;
        }

        element.textContent = vehicle.label;
      });
    root
      .querySelectorAll?.(
        "add-to-cart-component [data-force-vehicle-selection], [data-force-vehicle-selection]",
      )
      .forEach((container) => {
        if (container.dataset.canAddToCart !== "true") return;
        const button =
          container.matches?.("button") && container.type === "submit"
            ? container
            : container.querySelector?.(
                '[ref="addToCartButton"], button[type="submit"]',
              );
        if (button) button.disabled = !vehicle;
      });
  }

  function hideCatalogueProjectionFacets(root = document) {
    root
      .querySelectorAll?.(`input[name$="${CATALOGUE_FILTER_SUFFIX}"]`)
      .forEach((input) => {
        const facet =
          input.closest?.("accordion-custom") ?? input.closest?.("details");
        if (!facet) return;
        facet.hidden = true;
        facet.setAttribute?.("aria-hidden", "true");
        if (facet.dataset) facet.dataset.framewerkInternalFacet = "true";
      });
  }

  function mountRuntime(root = document) {
    hideCatalogueProjectionFacets(root);
    updateVehiclePresentation(root);
    root
      .querySelectorAll?.("[data-framewerk-comparison]")
      .forEach(mountComparison);
    root
      .querySelectorAll?.("[data-framewerk-comparison-toggle]")
      .forEach(mountComparisonToggle);
    root
      .querySelectorAll?.("[data-framewerk-quote-add]")
      .forEach(mountQuoteButton);
    root
      .querySelectorAll?.("[data-framewerk-quote-form]")
      .forEach(mountQuoteForm);
    updateQuoteCounters(root);
  }

  function updateQuoteCounters(root = document) {
    const count = api.quoteRequest
      .get()
      .reduce((total, item) => total + item.quantity, 0);
    root.querySelectorAll?.("[data-framewerk-quote-count]").forEach((node) => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
  }

  function mountComparisonToggle(button) {
    if (button.dataset.framewerkMounted === "true") return;
    button.dataset.framewerkMounted = "true";
    const handle = button.dataset.productHandle;
    if (!handle) {
      button.disabled = true;
      return;
    }

    button.addEventListener("click", () => {
      if (api.comparison.has(handle)) {
        api.comparison.remove(handle);
      } else {
        api.comparison.add({
          handle,
          productId: button.dataset.productId,
          variantId: button.dataset.variantId,
          title: button.dataset.productTitle,
          url: button.dataset.productUrl,
          image: button.dataset.productImage,
        });
      }
    });

    const render = (items) => {
      const selected = items.some((item) => item.handle === handle);
      button.setAttribute("aria-pressed", String(selected));
      button.disabled = items.length >= api.comparison.limit && !selected;
      button.textContent = selected
        ? (button.dataset.removeLabel ?? "Remove from comparison")
        : (button.dataset.addLabel ?? "Compare");
    };
    api.comparison.subscribe(render, { replayCurrent: true });
  }

  function mountQuoteButton(button) {
    if (button.dataset.framewerkMounted === "true") return;
    button.dataset.framewerkMounted = "true";
    const handle = button.dataset.productHandle;
    if (!handle) return;
    button.addEventListener("click", () => {
      if (api.quoteRequest.has(handle)) {
        api.quoteRequest.remove(handle);
      } else {
        api.quoteRequest.add({
          handle,
          productId: button.dataset.productId,
          variantId: button.dataset.variantId,
          title: button.dataset.productTitle,
          quantity: 1,
        });
      }
    });
    const render = (items) => {
      const selected = items.some((item) => item.handle === handle);
      button.setAttribute("aria-pressed", String(selected));
      button.textContent = selected
        ? (button.dataset.removeLabel ?? "Remove from quote")
        : (button.dataset.addLabel ?? "Request a quote");
    };
    api.quoteRequest.subscribe(render, { replayCurrent: true });
  }

  function mountQuoteForm(form) {
    if (form.dataset.framewerkMounted === "true") return;
    form.dataset.framewerkMounted = "true";
    const status = form.querySelector("[data-framewerk-quote-status]");
    const submit = form.querySelector('button[type="submit"]');
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const items = api.quoteRequest.get();
      if (!items.length) {
        if (status) status.textContent = "Add at least one product first.";
        return;
      }
      if (submit) submit.disabled = true;
      if (status) status.textContent = "Submitting quote request…";
      const fields = new FormData(form);
      try {
        const response = await fetch("/apps/framewerk?resource=quote", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            customerName: fields.get("customerName"),
            email: fields.get("email"),
            phone: fields.get("phone"),
            message: fields.get("message"),
            vehicleCode: api.vehicle.get()?.code ?? null,
            items,
          }),
        });
        const body = await response.json();
        if (!response.ok)
          throw new Error(body.error ?? "Quote submission failed.");
        api.quoteRequest.clear();
        form.reset();
        if (status) {
          status.textContent = `Quote request #${body.quoteRequest.id} was submitted.`;
        }
      } catch (error) {
        if (status) {
          status.textContent =
            error instanceof Error ? error.message : "Quote submission failed.";
        }
      } finally {
        if (submit) submit.disabled = false;
      }
    });
  }

  async function loadProduct(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) throw new Error(`Product ${handle} could not be loaded.`);
    return response.json();
  }

  function element(tag, text, className) {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    if (className) node.className = className;
    return node;
  }

  async function mountComparison(root) {
    if (root.dataset.framewerkMounted === "true") return;
    root.dataset.framewerkMounted = "true";
    const status = root.querySelector("[data-framewerk-comparison-status]");
    const grid = root.querySelector("[data-framewerk-comparison-grid]");

    async function render(items = api.comparison.get()) {
      if (!grid) return;
      grid.replaceChildren();
      if (!items.length) {
        if (status) status.textContent = "Choose products to compare.";
        return;
      }
      if (status) status.textContent = "Loading comparison…";
      const results = await Promise.allSettled(
        items.map((item) => loadProduct(item.handle)),
      );
      results.forEach((result, index) => {
        const saved = items[index];
        if (result.status !== "fulfilled") return;
        const product = result.value;
        const card = element("article", "", "framewerk-comparison__product");
        const title = element(
          "a",
          product.title,
          "framewerk-comparison__title",
        );
        title.href = product.url || `/products/${saved.handle}`;
        card.append(title);
        const variants = element(
          "select",
          "",
          "framewerk-comparison__variants",
        );
        for (const variant of product.variants ?? []) {
          const option = element("option", variant.title);
          option.value = String(variant.id);
          option.selected =
            String(saved.variantId ?? product.variants?.[0]?.id) ===
            option.value;
          variants.append(option);
        }
        variants.addEventListener("change", () =>
          api.comparison.update(saved.handle, { variantId: variants.value }),
        );
        card.append(variants);
        const add = element("button", "Add to cart");
        add.type = "button";
        add.addEventListener("click", async () => {
          add.disabled = true;
          try {
            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                items: [{ id: variants.value, quantity: 1 }],
              }),
            });
            if (!response.ok) throw new Error("Add to cart failed.");
            publish("framewerk:comparison:added-to-cart", {
              handle: saved.handle,
              variantId: variants.value,
            });
          } finally {
            add.disabled = false;
          }
        });
        card.append(add);
        const remove = element("button", "Remove");
        remove.type = "button";
        remove.addEventListener("click", () =>
          api.comparison.remove(saved.handle),
        );
        card.append(remove);
        grid.append(card);
      });
      if (status) {
        const failed = results.filter(
          (result) => result.status === "rejected",
        ).length;
        status.textContent = failed
          ? `${failed} comparison product${failed === 1 ? "" : "s"} could not be loaded.`
          : `${items.length} product${items.length === 1 ? "" : "s"} selected.`;
      }
    }

    const unsubscribe = api.comparison.subscribe(render, {
      replayCurrent: true,
    });
    root.framewerkUnmount = unsubscribe;
  }

  let readyResolve;
  const ready = new Promise((resolve) => {
    readyResolve = resolve;
  });

  const api = Object.freeze({
    version: VERSION,
    ready,
    events: EVENTS,
    storageKeys: KEYS,
    vehicle: Object.freeze({
      get: () => state.vehicle,
      select(vehicle) {
        const nextVehicle = stableVehicle(vehicle);
        if (JSON.stringify(nextVehicle) === JSON.stringify(state.vehicle)) {
          return state.vehicle;
        }
        state.vehicle = nextVehicle;
        write(KEYS.vehicle, state.vehicle);
        publish(EVENTS.vehicleChanged, state.vehicle);
        return state.vehicle;
      },
      clear() {
        if (!state.vehicle) return null;
        state.vehicle = null;
        write(KEYS.vehicle, null);
        write("framewerk:mister-fitment:selected-vehicle", null);
        publish(EVENTS.vehicleChanged, null);
        return null;
      },
      subscribe: (listener, options) =>
        subscribe(EVENTS.vehicleChanged, listener, options),
    }),
    garage: Object.freeze({
      get: () => [...state.garage],
      replace(vehicles) {
        const nextGarage = [];
        const seenCodes = new Set();
        for (const vehicle of Array.isArray(vehicles) ? vehicles : []) {
          let item;
          try {
            item = stableVehicle(vehicle);
          } catch {
            continue;
          }
          if (!item || seenCodes.has(item.code)) continue;
          seenCodes.add(item.code);
          nextGarage.push(item);
          if (nextGarage.length === 10) break;
        }
        if (JSON.stringify(nextGarage) === JSON.stringify(state.garage)) {
          return [...state.garage];
        }
        state.garage = nextGarage;
        write(KEYS.garage, state.garage);
        publish(EVENTS.garageChanged, [...state.garage]);
        return [...state.garage];
      },
      add(vehicle) {
        const item = stableVehicle(vehicle);
        state.garage = [
          item,
          ...state.garage.filter((saved) => saved.code !== item.code),
        ].slice(0, 10);
        write(KEYS.garage, state.garage);
        publish(EVENTS.garageChanged, [...state.garage]);
        return [...state.garage];
      },
      remove(code) {
        const nextGarage = state.garage.filter(
          (vehicle) => vehicle.code !== code,
        );
        if (nextGarage.length === state.garage.length) {
          return [...state.garage];
        }
        state.garage = nextGarage;
        write(KEYS.garage, state.garage);
        publish(EVENTS.garageChanged, [...state.garage]);
        return [...state.garage];
      },
      subscribe: (listener, options) =>
        subscribe(EVENTS.garageChanged, listener, options),
    }),
    comparison: collection(
      "comparison",
      KEYS.comparison,
      EVENTS.comparisonChanged,
      4,
    ),
    quoteRequest: collection("quote", KEYS.quote, EVENTS.quoteChanged, 50),
    compatibility: Object.freeze({
      async getProductCompatibility(productId, vehicle = state.vehicle) {
        if (!vehicle)
          return { productId, vehicleCode: null, status: "unselected" };
        const url = new URL("/apps/framewerk", window.location.origin);
        url.searchParams.set("resource", "fitment");
        url.searchParams.set("product_id", String(productId));
        url.searchParams.set("vehicle_code", String(vehicle.code));
        const response = await fetch(url);
        if (!response.ok)
          throw new Error(`Compatibility request failed (${response.status}).`);
        return response.json();
      },
    }),
    catalogue: Object.freeze({
      refresh() {
        queueCatalogueVehicle(state.vehicle);
      },
      clearVehicleConstraint() {
        api.vehicle.clear();
      },
      buildUrl(vehicle, currentHref) {
        return buildCatalogueUrl(vehicle, currentHref).url.toString();
      },
    }),
    lifecycle: Object.freeze({
      mount(root = document) {
        mountRuntime(root);
        root.dispatchEvent(new CustomEvent(EVENTS.ready, { bubbles: true }));
      },
      unmount() {},
      afterSectionRender(root = document) {
        publish(EVENTS.sectionRendered, root);
        api.lifecycle.mount(root);
        void websiteSettingsController.apply(root);
        queueCatalogueVehicle(state.vehicle);
      },
    }),
    subscribe,
  });

  window.Framewerk = api;
  subscribe(EVENTS.vehicleChanged, () => updateVehiclePresentation(document));
  subscribe(EVENTS.vehicleChanged, queueCatalogueVehicle);
  subscribe(EVENTS.quoteChanged, () => updateQuoteCounters(document));
  document.addEventListener?.("shopify:section:load", (event) => {
    const root = event.target instanceof HTMLElement ? event.target : document;
    api.lifecycle.afterSectionRender(root);
  });
  window.addEventListener?.("pageshow", () => api.lifecycle.mount(document));
  readyResolve();
  publish(EVENTS.ready, api);
  const publishInitialState = () => {
    // Compatibility surfaces outside the shared runtime still use this event
    // to initialise their selected-vehicle state. The selector now builds the
    // final filtered landing URL up front, so this signal no longer needs to
    // trigger a second navigation.
    window.dispatchEvent(
      new CustomEvent("framewerk:mister-fitment:vehicle-changed", {
        detail: state.vehicle,
      }),
    );
    publish(EVENTS.vehicleChanged, state.vehicle);
    publish(EVENTS.garageChanged, [...state.garage]);
    publish(EVENTS.comparisonChanged, [...state.comparison]);
    publish(EVENTS.quoteChanged, [...state.quote]);
    api.lifecycle.mount(document);
  };
  const publishReconciledVehicle = () => {
    window.dispatchEvent(
      new CustomEvent("framewerk:mister-fitment:vehicle-changed", {
        detail: state.vehicle,
      }),
    );
    publish(EVENTS.vehicleChanged, state.vehicle);
  };
  const reconcileInBackground = () => {
    void reconcileStoredVehicle().then((changed) => {
      if (changed) publishReconciledVehicle();
    });
  };

  queueMicrotask(async () => {
    const cachedCatalogue = readVehicleCatalogueCache();
    if (cachedCatalogue) {
      await reconcileStoredVehicle(cachedCatalogue);
    }
    publishInitialState();
    void websiteSettingsController.apply(document);

    if (!cachedCatalogue && state.vehicle) {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(reconcileInBackground, { timeout: 1500 });
      } else if (typeof window.setTimeout === "function") {
        window.setTimeout(reconcileInBackground, 100);
      } else {
        queueMicrotask(reconcileInBackground);
      }
    }
  });
})();
